// ===================================================
// GAM Command Center — ObjectRef Inline Node
// Renders an inline chip referencing another entity
// ===================================================

import { Node, mergeAttributes } from '@tiptap/core';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    objectRef: {
      setObjectRef: (attrs: {
        id: string;
        entityType: string | null;
        title: string;
      }) => ReturnType;
    };
  }

  interface Storage {
    objectRef: {
      onNavigate: null | ((ref: { id: string; entityType: string | null; title: string }) => void);
      onRequestInsert: null | (() => void);
    };
  }
}

export const ObjectRef = Node.create({
  name: 'objectRef',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: false,

  addStorage() {
    return {
      onNavigate: null as null | ((ref: { id: string; entityType: string | null; title: string }) => void),
      onRequestInsert: null as null | (() => void),
    };
  },

  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-id') || '',
      },
      entityType: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute('data-entity-type');
          return v && v !== 'null' ? v : null;
        },
      },
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') || '',
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-type="object-ref"]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        {
          'data-type': 'object-ref',
          'data-id': node.attrs.id,
          'data-entity-type': node.attrs.entityType || '',
          'data-title': node.attrs.title,
          class: 'gam-object-ref',
        },
        HTMLAttributes
      ),
      node.attrs.title || node.attrs.id,
    ];
  },

  addNodeView() {
    const editor = this.editor;

    return ({ node }) => {
      const dom = document.createElement('span');
      dom.classList.add('gam-object-ref');
      dom.setAttribute('data-type', 'object-ref');
      dom.setAttribute('data-id', node.attrs.id);
      if (node.attrs.entityType) {
        dom.setAttribute('data-entity-type', node.attrs.entityType);
      }
      dom.contentEditable = 'false';

      const badge = document.createElement('span');
      badge.classList.add('gam-object-ref__type');
      badge.textContent = node.attrs.entityType || 'ref';

      const label = document.createElement('span');
      label.classList.add('gam-object-ref__title');
      label.textContent = node.attrs.title || node.attrs.id;

      dom.appendChild(badge);
      dom.appendChild(label);

      dom.addEventListener('click', () => {
        editor.storage.objectRef?.onNavigate?.({
          id: node.attrs.id,
          entityType: node.attrs.entityType,
          title: node.attrs.title,
        });
      });

      return { dom };
    };
  },

  addCommands() {
    return {
      setObjectRef:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs,
          });
        },
    };
  },
});
